import React from 'react';
import { Mail, Send, AlertCircle, CheckCircle } from 'lucide-react';

export default function EmailTab({ 
  emailSettings, 
  setEmailSettings, 
  emailPasswordSet,
  handleEmailSettingsUpdate,
  testEmailAddress,
  setTestEmailAddress,
  testEmailConfig,
  testingEmailSending
}) {
  return (
    <div data-testid="email-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-6 flex items-center">
        <Mail className="h-7 w-7 mr-3" />
        Email Settings
      </h2>

      {/* Status Card */}
      <div className={`mb-6 p-4 rounded-lg border ${
        emailSettings.is_enabled && emailPasswordSet
          ? 'bg-green-50 border-green-200' 
          : 'bg-yellow-50 border-yellow-200'
      }`}>
        <div className="flex items-center">
          {emailSettings.is_enabled && emailPasswordSet ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800">Email notifications are configured and ready</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <span className="text-yellow-800">
                Email not configured. Enter your SMTP settings below.
              </span>
            </>
          )}
        </div>
      </div>

      {/* Gmail App Password Instructions */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-medium text-blue-800 mb-2">Gmail Setup Instructions</h3>
        <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
          <li>Enable 2-Step Verification in your Google Account</li>
          <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">Google App Passwords</a></li>
          <li>Select "Mail" and your device, then click "Generate"</li>
          <li>Copy the 16-character password and paste it below</li>
        </ol>
      </div>

      {/* SMTP Configuration Form */}
      <form onSubmit={handleEmailSettingsUpdate} className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
        <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4">
          SMTP Configuration
        </h3>

        <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <h4 className="font-medium text-gray-900">Enable Email Notifications</h4>
            <p className="text-sm text-gray-500">Send order confirmations, status updates via email</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={emailSettings.is_enabled}
              onChange={(e) => setEmailSettings({ ...emailSettings, is_enabled: e.target.checked })}
              className="sr-only peer"
              data-testid="email-enabled-toggle"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1a3a5c]"></div>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
            <input
              type="text"
              value={emailSettings.smtp_host}
              onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="smtp.gmail.com"
              data-testid="smtp-host-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
            <input
              type="number"
              value={emailSettings.smtp_port}
              onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="587"
              data-testid="smtp-port-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              value={emailSettings.smtp_email}
              onChange={(e) => setEmailSettings({ ...emailSettings, smtp_email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="your@gmail.com"
              data-testid="smtp-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Password
              {emailPasswordSet && <span className="text-green-600 ml-2">(Set)</span>}
            </label>
            <input
              type="password"
              value={emailSettings.smtp_password}
              onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder={emailPasswordSet ? "••••••••••••••••" : "Enter 16-character App Password"}
              data-testid="smtp-password-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use a 16-character App Password from Google (requires 2FA enabled)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sender Name</label>
            <input
              type="text"
              value={emailSettings.sender_name}
              onChange={(e) => setEmailSettings({ ...emailSettings, sender_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Marissa Gold And Diamonds"
              data-testid="sender-name-input"
            />
          </div>
        </div>

        {/* Email Type Toggles */}
        <div className="mt-6 border-t border-gray-200 pt-6">
          <h4 className="font-medium text-gray-900 mb-4">Email Notifications to Send</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">Order Confirmation</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.order_confirmation_enabled}
                  onChange={(e) => setEmailSettings({ ...emailSettings, order_confirmation_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#1a3a5c]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">Order Status Updates</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.order_status_enabled}
                  onChange={(e) => setEmailSettings({ ...emailSettings, order_status_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#1a3a5c]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">Password Reset</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.password_reset_enabled}
                  onChange={(e) => setEmailSettings({ ...emailSettings, password_reset_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#1a3a5c]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">Product Back in Stock</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.notify_me_enabled}
                  onChange={(e) => setEmailSettings({ ...emailSettings, notify_me_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#1a3a5c]"></div>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="bg-[#1a3a5c] text-white px-6 py-2 rounded-sm hover:bg-[#1f4268] flex items-center space-x-2"
            data-testid="save-email-settings"
          >
            <span>Save Email Settings</span>
          </button>
        </div>
      </form>

      {/* Test Email Section */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4">Send Test Email</h3>
        <p className="text-sm text-gray-600 mb-4">
          Enter an email address to send a test email and verify your configuration.
        </p>
        <div className="flex space-x-3">
          <input
            type="email"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
            placeholder="test@example.com"
            data-testid="test-email-input"
          />
          <button
            type="button"
            onClick={testEmailConfig}
            disabled={testingEmailSending || !testEmailAddress}
            className="px-6 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            data-testid="send-test-email"
          >
            <Send className="h-4 w-4" />
            <span>{testingEmailSending ? 'Sending...' : 'Send Test'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
